import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {AfterMatchEvent} from "@/modules/match/events/AfterMatchEvent";
import CompetitionPlayerLogger from "@/modules/competitions/services/CompetitionPlayerLogger";
import {Season} from "@/modules/season/entity";
import {CardIncident, GoalIncident, Incident} from "@/modules/match/entity";
import {Player, Team} from "@/modules/team/entity";
import {CardEnum} from "@/modules/match/enums";
import SuspensionHandler from "@/modules/competitions/services/SuspensionHandler";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {AfterCreateGameEvent} from "@/modules/base/events/AfterCreateGameEvent";
import {Competition, CompetitionPlayerLog, Suspension} from "@/modules/competitions/entity";
import chance from "@/modules/base/services/chance";

@injectable()
export class CompetitionLogEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) protected configRepository: ConfigRepository,
    @inject(CompetitionPlayerLogger) protected competitionPlayerLogger: CompetitionPlayerLogger,
    @inject(SuspensionHandler) protected suspensionHandler: SuspensionHandler,
  ) {}

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterMatchEvent, callback: this.handleExistingSuspensions},
      {event: AfterMatchEvent, callback: this.onAfterMatchEvent},
      {event: AfterCreateGameEvent, callback: this.onAfterCreateGameEvent},
    ];
  }

  async handleExistingSuspensions(event: AfterMatchEvent): Promise<void> {
    console.time('CompetitionLogEventSubscriber::handleExistingSuspensions');
    const playerIds = [
      ...event.match.homeTeam.players,
      ...event.match.awayTeam.players,
    ].map(p => p.id);

    const suspensions = await Suspension
      .createQueryBuilder('suspension')
      .innerJoin('suspension.player', 'player')
      .innerJoin('suspension.competition', 'competition')
      .where(`player.id IN (${playerIds.join(',')})`)
      .andWhere('competition.id = :competitionId', {competitionId: event.competition.id})
      .getMany();

    await suspensions.forEachAsync(async (suspension: Suspension) => this.suspensionHandler.handle(suspension));
    console.timeEnd('CompetitionLogEventSubscriber::handleExistingSuspensions');
  }

  async onAfterCreateGameEvent(event: AfterCreateGameEvent): Promise<void> {
    const season = await Season.current();
    const oldSeasons = await Season
      .createQueryBuilder('season')
      .where('season.current = false')
      .getMany();

    const competitions = await Competition
      .createQueryBuilder('competition')
      .leftJoinAndSelect('competition.divisions', 'division')
      .leftJoinAndSelect('division.teams', 'team')
      .leftJoinAndSelect('team.players', 'player')
      .getMany();

    await competitions.forEachAsync(async (competition: Competition) => {
      await collect(competition.divisions.map(d => d.teams))
        .flatten(1)
        .unique((team: Team) => team.id)
        .toArray()
        .forEachAsync(async (team: Team) => {
          await team.players.forEachAsync(async (player: Player) => {
            const log = new CompetitionPlayerLog();
            log.season = season;
            log.competition = competition;
            log.player = player;
            await log.save();

            if (!this.configRepository.get('debug')) {
              return;
            }

            await oldSeasons
              .forEachAsync(async (season: Season) => {
                const log = new CompetitionPlayerLog();
                log.season = season;
                log.competition = competition;
                log.player = player;
                log.matches = chance.integer({min: 5, max: 34});
                log.goals = chance.integer({min: 0, max: 40});
                log.yellow = chance.integer({min: 0, max: 15});
                log.yellowRed = chance.integer({min: 0, max: 5});
                log.red = chance.integer({min: 0, max: 5});
                await log.save();
              });
          });
        });
    });
  }

  async onAfterMatchEvent(event: AfterMatchEvent): Promise<void> {
    console.time('CompetitionLogEventSubscriber::onAfterMatchEvent');
    const season = await Season.current();

    const incidents = await Incident
      .createQueryBuilder('incident')
      .innerJoin('incident.match', 'match')
      .where('match.id = :matchId', {matchId: event.match.id})
      .getMany();

    const players = [
      ...event.match.homeTeam.players.filter(p => p.lineup !== null),
      ...event.match.awayTeam.players.filter(p => p.lineup !== null),
    ];

    const logs = await this.competitionPlayerLogger.getLogs(players, event.competition);

    await players.forEachAsync(async (player: Player) => {
      const playersIncidents = incidents.filter(i => i.playerId === player.id);

      const yellow = playersIncidents.has(i => i instanceof CardIncident && i.card === CardEnum.YELLOW);
      const yellowRed = playersIncidents.has(i => i instanceof CardIncident && i.card === CardEnum.YELLOW_RED);
      const red = playersIncidents.has(i => i instanceof CardIncident && i.card === CardEnum.RED);
      const goals = playersIncidents.count(i => i instanceof GoalIncident && !i.ownGoal);

      const log = logs.find(l => l.playerId === player.id)
        || await this.competitionPlayerLogger.createLog(season, player, event.competition);
      await this.competitionPlayerLogger.logByLog(log, goals, yellow, yellowRed, red);

      let suspensionType: CardEnum | false = false;

      if (yellow && log.yellow % this.configRepository.get('competitions.suspensions.yellowLimit') === 0) {
        suspensionType = CardEnum.YELLOW;
      }

      if (yellowRed) {
        suspensionType = CardEnum.YELLOW_RED;
      }

      if (red) {
        suspensionType = CardEnum.RED;
      }

      if (suspensionType !== false) {
        await this.suspensionHandler.suspend(player, event.competition, suspensionType);
      }
    });
    console.timeEnd('CompetitionLogEventSubscriber::onAfterMatchEvent');
  }
}